import { useCallback } from "react";
import { CalendarNav, formatDate, MbscCalendarDayData, MbscCalendarEventData } from "@mobiscroll/react";
import { OwcIconButton, OwcListItem, OwcSelectDropdown } from "@one/react";
import { react18WorkaroundProps } from "../../../../utils/constants/constants";
import { VIEW_OPTIONS } from "../constants";
import { EventData, ResourceData } from "../types";
import ResourceHeader from "../components/ResourceHeader";
import ResourceItem from "../components/ResourceItem";
import ScheduleEvent from "../components/ScheduleEvent";

interface UseCalendarRenderersParams {
  navigateToToday: () => void;
  navigateToPrev: () => void;
  navigateToNext: () => void;
  handleViewChange: (ev: CustomEvent) => void;
  view: string;
  isSelected: (id: string) => boolean;
  toggleResource: (id: string, selected: boolean) => void;
  resources: ResourceData[];
  selectedCount: number;
  handleSelectAll: (selected: boolean) => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  siteTimezone: string;
}

const useCalendarRenderers = ({
  navigateToToday,
  navigateToPrev,
  navigateToNext,
  handleViewChange,
  view,
  isSelected,
  toggleResource,
  resources,
  selectedCount,
  handleSelectAll,
  isAllSelected,
  isIndeterminate,
  siteTimezone,
}: UseCalendarRenderersParams) => {
  const renderHeader = useCallback(
    () => (
      <>
        <div className="md-work-week-today custom-nav-button today-button" onClick={navigateToToday} title="Go to today">
          Today
        </div>
        <OwcIconButton
          flat
          onClick={navigateToPrev}
          icon="chevron_left"
          elevated={false}
          disableRipple
          className="md-work-week-prev mobi-custom-nav-button"
          {...react18WorkaroundProps}
        />
        <OwcIconButton
          flat
          onClick={navigateToNext}
          icon="chevron_right"
          elevated={false}
          disableRipple
          className="md-work-week-prev mobi-custom-nav-button"
          {...react18WorkaroundProps}
        />
        <CalendarNav className="md-work-week-nav" />
        <div className="change-end-time-modal__view-select">
          <OwcSelectDropdown
            style={{ width: 154 }}
            size="compact"
            outlined
            onSelectChange={handleViewChange}
            {...react18WorkaroundProps}
          >
            {VIEW_OPTIONS.map((option) => (
              <OwcListItem
                key={option.value}
                selected={view === option.value}
                {...react18WorkaroundProps}
              >
                {option.label}
              </OwcListItem>
            ))}
          </OwcSelectDropdown>
        </div>
      </>
    ),
    [navigateToToday, navigateToPrev, navigateToNext, handleViewChange, view],
  );

  const renderResource = useCallback(
    (resource: ResourceData) => (
      <ResourceItem resource={resource} isSelected={isSelected(resource.id as string)} onSelectionChange={toggleResource} />
    ),
    [isSelected, toggleResource],
  );

  const renderResourceHeader = useCallback(
    () => (
      <ResourceHeader
        totalEquipment={resources.length}
        selectedCount={selectedCount}
        onSelectAll={handleSelectAll}
        isAllSelected={isAllSelected}
        isIndeterminate={isIndeterminate}
      />
    ),
    [resources.length, selectedCount, handleSelectAll, isAllSelected, isIndeterminate],
  );

  const renderHour = useCallback(
    (args: MbscCalendarDayData) => <div className="md-date-header-hour">{formatDate("h:mm A", args.date)}</div>,
    [],
  );

  const renderScheduleEvent = useCallback(
    (data: MbscCalendarEventData) => <ScheduleEvent event={data.original as EventData} siteTimezone={siteTimezone} />,
    [siteTimezone],
  );

  return { renderHeader, renderResource, renderResourceHeader, renderHour, renderScheduleEvent };
};

export default useCalendarRenderers;
